import { CreateDownsellDataParams } from "../../adapters/create-downsell";
import { GetBookingDownsellResponse } from "../../adapters/get-booking-downsell";

export type DownsellFlowProviderProps = {
  children: React.ReactNode;
};

export type DownsellType =
  | "booking-only"
  | "booking-and-cycle"
  | "booking-and-next-bookings"
  | "booking-and-next-bookings-and-cycles"
  | "only-cycles";

export enum DownsellFlowStep {
  CREATE = "DOWNSELL_CREATE",
  LOADING = "DOWNSELL_LOADING",
  RESULT = "DOWNSELL_RESULT",
}

export type DownsellFlowStatus = {
  booking: "success" | "error" | null;
  downsell: "success" | "error" | null;
};

export type ErrorMessages = {
  booking?: string;
  downsell?: string;
};

export type FlowStep = DownsellFlowStep;

export const FlowTransitions: Record<
  FlowStep,
  {
    next: FlowStep;
    previous?: FlowStep;
  }
> = {
  [DownsellFlowStep.CREATE]: {
    next: DownsellFlowStep.LOADING,
  },
  [DownsellFlowStep.LOADING]: {
    next: DownsellFlowStep.RESULT,
    previous: DownsellFlowStep.CREATE,
  },
  [DownsellFlowStep.RESULT]: {
    next: DownsellFlowStep.CREATE,
  },
} as const;

export type DownsellDataParams = {
  downsell_type: DownsellType;
};

export type DownsellBookingData = {
  id: number;
  privateOfficeLabel: string;
  privateOfficeId: number;
  credits: number;
  take: number;
  members: number;
};

export type DownsellCompanyData = {
  id: number;
  name: string;
};

type creditsDescription = {
  amount: number;
  courtesy: number;
  exceededPrice: number;
  unitPrice: number;
  consumption: {
    [key: string]: number;
  };
};

export type PackageVersion = {
  id: number;
  status: string;
  startDate: string;
  endDate: string;
  packageId: number;
  credits: creditsDescription;
  anticipatedCreditsReceived: number;
  anticipatedCredits: number;
};

export type DownsellDetailsData = {
  booking: {
    id: number;
    privateOfficeLabel: string;
    privateOfficeId: number;
    credits: number;
    take: string;
    members: number;
  };
  company: {
    id: number;
    name: string;
  };
  cycleDetails: {
    id: number;
    startDate: string;
    endDate: string;
    credits: number;
    exceededPrice: number;
    courtesy: number;
    unitPrice: number;
    packageId: number;
  };
  packageDetails: {
    id: number;
    name: string;
    duration: string;
    isCompanyWide: boolean;
    products: string[];
    versions: PackageVersion[];
    groups: { name: string }[];
  };
};

export type SimulateDownsellProps = {
  companyId: number;
  packageId: number;
  bookingId?: number;
  cancelStrategy?: DownsellType;
  poContractId?: number;
  cancelledAt?: string;
};

export type SimulatedDownsellResponse = {
  cancelledBookings?: CancelledBooking[];
  updatedCycles?: UpdatedCycle[];
};

export type CancelledBooking = {
  id: number;
  userId: any;
  spotId: number;
  type: number;
  bookingDate: string;
  status: number;
  credits: number;
  exemptCredits: any;
  packageVersionId: number;
  companyId: number;
  groupId: any;
  updatedAt: string;
};

export type UpdatedCycle = {
  bookingId: number;
  cycleId: number;
  startDate: string;
  endDate: string;
  oldCapacity: number;
  oldAmount: number;
  newCapacity: number;
  newAmount: number;
  hasInstallmentWithEnoughCredits: boolean;
  hasPaidOrInvoicedInstallments: boolean;
};

export type ProcessingStatus = {
  currentOperation: "booking" | "downsell";
};

export type DownsellFlowContextData = {
  currentStep: FlowStep;
  setCurrentStep: (step: FlowStep) => void;
  handleSubmitDownsellData: (data: DownsellDataParams) => void;
  downsellData: DownsellDataParams | null | undefined;
  nextStep: () => void;
  handleMapDownsellData: (data: GetBookingDownsellResponse) => void;
  downsellDetailsData: DownsellDetailsData | null | undefined;
  handleBookingDownsellSimulation: (data: SimulateDownsellProps) => void;
  simulatedDownsell: SimulatedDownsellResponse | null | undefined;
  handleCreateDownsell: (
    data: CreateDownsellDataParams,
  ) => Promise<boolean | undefined>;
  loadingStatus: boolean;
  downsellFlowStatus: DownsellFlowStatus;
  setDownsellFlowStatus: (status: Partial<DownsellFlowStatus>) => void;
  errorMessages: ErrorMessages;
  setErrorMessages: (messages: Partial<ErrorMessages>) => void;
  processingStatus: ProcessingStatus;
};
